import React from 'react';
import { Zap, Droplets, Check, X, AlertTriangle, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
export function SystemComparisonSection() {
  return <section id="system-comparison" className="py-16 bg-white md:py-24" data-id="element-1036">
      <div className="container mx-auto px-4 md:px-6" data-id="element-1037">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1038">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl" data-id="element-1039">
            Electric vs. Hydronic: The Honest Breakdown
          </h2>
          <p className="mt-4 text-lg text-slate-600" data-id="element-1040">
            Both systems melt snow effectively. The right choice depends on your
            property size, energy rates, and installation constraints.
          </p>
        </div>

        {/* Comparison Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-1041">
          {/* Electric System */}
          <Card className="border-2 border-slate-100 shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden" data-id="element-1042">
            <div className="absolute top-0 left-0 w-full h-2 bg-yellow-400" data-id="element-1043"></div>
            <CardHeader className="pb-2" data-id="element-1044">
              <div className="flex items-center gap-3 mb-2" data-id="element-1045">
                <div className="p-3 rounded-full bg-yellow-100 text-yellow-600" data-id="element-1046">
                  <Zap className="h-6 w-6" data-id="element-1047" />
                </div>
                <CardTitle className="text-2xl" data-id="element-1048">Electric Systems</CardTitle>
              </div>
              <p className="text-slate-500" data-id="element-1049">
                Heating cables embedded directly in the surface. Simple,
                responsive, and maintenance-free.
              </p>
            </CardHeader>
            <CardContent className="space-y-6" data-id="element-1050">
              <div data-id="element-1051">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2" data-id="element-1052">
                  <Check className="h-4 w-4 text-green-500" data-id="element-1053" /> Best For:
                </h4>
                <ul className="space-y-2 text-sm text-slate-600" data-id="element-1054">
                  <li className="flex items-start gap-2" data-id="element-1055">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" data-id="element-1056"></span>
                    Residential driveways & walkways
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1057">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" data-id="element-1058"></span>
                    Retrofitting existing surfaces (sometimes)
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1059">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" data-id="element-1060"></span>
                    Projects needing lower upfront cost
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1061">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" data-id="element-1062"></span>
                    Precise zoning (e.g., tire tracks only)
                  </li>
                </ul>
              </div>

              <div data-id="element-1063">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2" data-id="element-1064">
                  <AlertTriangle className="h-4 w-4 text-amber-500" data-id="element-1065" />{' '}
                  Watch-Outs:
                </h4>
                <ul className="space-y-2 text-sm text-slate-600" data-id="element-1066">
                  <li className="flex items-start gap-2" data-id="element-1067">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" data-id="element-1068"></span>
                    Operating costs can be higher for very large areas
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1069">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" data-id="element-1070"></span>
                    Requires adequate electrical panel capacity (200A+)
                  </li>
                </ul>
              </div>

              <div className="bg-red-50 p-4 rounded-lg border border-red-100" data-id="element-1071">
                <h4 className="font-semibold text-red-800 mb-1 flex items-center gap-2 text-sm" data-id="element-1072">
                  <X className="h-4 w-4" data-id="element-1073" /> Bad Fit When:
                </h4>
                <p className="text-xs text-red-700" data-id="element-1074">
                  You have a massive commercial lot (2000+ sq ft) or extremely
                  high electricity rates with no off-peak options.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Hydronic System */}
          <Card className="border-2 border-slate-100 shadow-lg hover:shadow-xl transition-shadow relative overflow-hidden" data-id="element-1075">
            <div className="absolute top-0 left-0 w-full h-2 bg-blue-500" data-id="element-1076"></div>
            <CardHeader className="pb-2" data-id="element-1077">
              <div className="flex items-center gap-3 mb-2" data-id="element-1078">
                <div className="p-3 rounded-full bg-blue-100 text-blue-600" data-id="element-1079">
                  <Droplets className="h-6 w-6" data-id="element-1080" />
                </div>
                <CardTitle className="text-2xl" data-id="element-1081">Hydronic Systems</CardTitle>
              </div>
              <p className="text-slate-500" data-id="element-1082">
                Heated fluid circulating through tubing. Efficient for large
                scale, but complex to install.
              </p>
            </CardHeader>
            <CardContent className="space-y-6" data-id="element-1083">
              <div data-id="element-1084">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2" data-id="element-1085">
                  <Check className="h-4 w-4 text-green-500" data-id="element-1086" /> Best For:
                </h4>
                <ul className="space-y-2 text-sm text-slate-600" data-id="element-1087">
                  <li className="flex items-start gap-2" data-id="element-1088">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" data-id="element-1089"></span>
                    Large driveways & commercial areas (1000+ sq ft)
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1090">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" data-id="element-1091"></span>
                    New construction projects
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1092">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" data-id="element-1093"></span>
                    Lowest possible long-term operating costs
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1094">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" data-id="element-1095"></span>
                    Homes with existing boiler infrastructure
                  </li>
                </ul>
              </div>

              <div data-id="element-1096">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2" data-id="element-1097">
                  <AlertTriangle className="h-4 w-4 text-amber-500" data-id="element-1098" />{' '}
                  Watch-Outs:
                </h4>
                <ul className="space-y-2 text-sm text-slate-600" data-id="element-1099">
                  <li className="flex items-start gap-2" data-id="element-1100">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" data-id="element-1101"></span>
                    Significantly higher upfront installation cost
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1102">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" data-id="element-1103"></span>
                    Requires space for boiler, pumps, and manifolds
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1104">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" data-id="element-1105"></span>
                    More mechanical parts = more maintenance
                  </li>
                </ul>
              </div>

              <div className="bg-red-50 p-4 rounded-lg border border-red-100" data-id="element-1106">
                <h4 className="font-semibold text-red-800 mb-1 flex items-center gap-2 text-sm" data-id="element-1107">
                  <X className="h-4 w-4" data-id="element-1108" /> Bad Fit When:
                </h4>
                <p className="text-xs text-red-700" data-id="element-1109">
                  You have a small standard driveway, a tight upfront budget, or
                  no space for mechanical equipment.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Comparison Table */}
        <div className="overflow-x-auto" data-id="element-1110">
          <table className="w-full text-sm text-left border-collapse" data-id="element-1111">
            <thead data-id="element-1112">
              <tr className="bg-slate-50 border-b border-slate-200" data-id="element-1113">
                <th className="p-4 font-semibold text-slate-900 w-1/3" data-id="element-1114">
                  Feature
                </th>
                <th className="p-4 font-semibold text-slate-900 w-1/3 border-l border-slate-200" data-id="element-1115">
                  <div className="flex items-center gap-2" data-id="element-1116">
                    <Zap className="h-4 w-4 text-yellow-500" data-id="element-1117" /> Electric
                  </div>
                </th>
                <th className="p-4 font-semibold text-slate-900 w-1/3 border-l border-slate-200" data-id="element-1118">
                  <div className="flex items-center gap-2" data-id="element-1119">
                    <Droplets className="h-4 w-4 text-blue-500" data-id="element-1120" /> Hydronic
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200" data-id="element-1121">
              <tr data-id="element-1122">
                <td className="p-4 font-medium text-slate-700" data-id="element-1123">
                  Install Complexity
                </td>
                <td className="p-4 text-slate-600 border-l border-slate-200" data-id="element-1124">
                  Low to Medium
                </td>
                <td className="p-4 text-slate-600 border-l border-slate-200" data-id="element-1125">
                  High (Requires plumber & electrician)
                </td>
              </tr>
              <tr data-id="element-1126">
                <td className="p-4 font-medium text-slate-700" data-id="element-1127">Upfront Cost</td>
                <td className="p-4 text-slate-600 border-l border-slate-200" data-id="element-1128">
                  Lower
                </td>
                <td className="p-4 text-slate-600 border-l border-slate-200" data-id="element-1129">
                  Higher
                </td>
              </tr>
              <tr data-id="element-1130">
                <td className="p-4 font-medium text-slate-700" data-id="element-1131">
                  Operating Cost
                </td>
                <td className="p-4 text-slate-600 border-l border-slate-200" data-id="element-1132">
                  Moderate (depends on electricity rates)
                </td>
                <td className="p-4 text-slate-600 border-l border-slate-200" data-id="element-1133">
                  Low (gas/propane usually cheaper)
                </td>
              </tr>
              <tr data-id="element-1134">
                <td className="p-4 font-medium text-slate-700" data-id="element-1135">Maintenance</td>
                <td className="p-4 text-slate-600 border-l border-slate-200" data-id="element-1136">
                  Zero (No moving parts)
                </td>
                <td className="p-4 text-slate-600 border-l border-slate-200" data-id="element-1137">
                  Annual (Boiler/pump check-up)
                </td>
              </tr>
              <tr data-id="element-1138">
                <td className="p-4 font-medium text-slate-700" data-id="element-1139">Lifespan</td>
                <td className="p-4 text-slate-600 border-l border-slate-200" data-id="element-1140">
                  30-50 Years
                </td>
                <td className="p-4 text-slate-600 border-l border-slate-200" data-id="element-1141">
                  30-50 Years (Boiler ~15-20 yrs)
                </td>
              </tr>
              <tr data-id="element-1142">
                <td className="p-4 font-medium text-slate-700" data-id="element-1143">
                  Retrofit Friendly?
                </td>
                <td className="p-4 text-slate-600 border-l border-slate-200" data-id="element-1144">
                  Yes (Can be laid over existing in some cases)
                </td>
                <td className="p-4 text-slate-600 border-l border-slate-200" data-id="element-1145">
                  No (Requires deep excavation)
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>;
}